import type { RemoteOptionsState } from "../../hooks/useRemoteOptions.js";
import type { ComboboxOption } from "./Combobox.types.js";

/* The rows ComboboxListbox renders, as data. The static path fills `options`
   only; the async path derives the rest from the shared request state. */

export interface ListboxLine {
  readonly text: string;
  readonly failed?: boolean;
}

/** What the open listbox shows. Everything but `options` is async only. */
export interface ComboboxListboxRows {
  readonly options: ReadonlyArray<ComboboxOption>;
  /** The line above the options: hint, loading, failed or empty. */
  readonly message?: ListboxLine | undefined;
  /** Retry or Load more — an option row one index past the last option. */
  readonly action?: { readonly label: string; readonly run: () => void };
  /** The line under the action: a failed Load more. */
  readonly footnote?: ListboxLine | undefined;
  /** What the field's polite status region says about these rows. A listbox
   *  may own only options, so the live region cannot sit inside it. */
  readonly status?: string | undefined;
  /** A page is on its way (`aria-busy` on the listbox). */
  readonly busy?: boolean;
}

const line = (text: string, failed = false): ListboxLine =>
  failed ? { text, failed } : { text };

/** The async listbox's rows, derived from the shared request state. */
export function remoteListboxRows(
  remote: RemoteOptionsState<ComboboxOption>,
  emptyMessage: string,
): ComboboxListboxRows {
  const { remaining, options } = remote;
  if (remaining > 0) {
    const noun = remaining === 1 ? "character" : "characters";
    const text = `Type ${remaining} more ${noun}.`;
    return { options: [], message: line(text), status: text };
  }
  if (remote.loading) {
    const text = "Loading options…";
    return { options: [], message: line(text), status: text, busy: true };
  }
  if (remote.failed) {
    const text = "Options could not be loaded.";
    return {
      options: [],
      message: line(text, true),
      action: { label: "Retry", run: remote.retry },
      status: text,
    };
  }
  if (options.length === 0) {
    return { options, message: line(emptyMessage), status: emptyMessage };
  }
  const moreFailed = "More options could not be loaded.";
  return {
    options,
    ...(remote.nextCursor
      ? {
          action: {
            label: remote.loadMoreFailed
              ? "Try again"
              : remote.loadingMore
                ? "Loading more…"
                : "Load more",
            run: remote.loadNextPage,
          },
        }
      : {}),
    footnote: remote.loadMoreFailed ? line(moreFailed, true) : undefined,
    busy: remote.loadingMore,
    status: remote.loadMoreFailed
      ? moreFailed
      : remote.loadingMore
        ? "Loading more options…"
        : `${options.length} ${options.length === 1 ? "option" : "options"}.`,
  };
}
